import strings from "global/constants/StringConstants";

/**
 * Types, limits and file checks for Compare contract versions.
 *
 * The `Contract*` / `Compare*` wire shapes mirror the AI service's JSON one-to-one (core passes it through
 * unchanged -- `contracts/contract-version-diff/fe-core.md` §2), so a field name here is a field name on the wire.
 * The `Compare*` view-model shapes are what the screen renders, built by `ContractCompareHelper`.
 */

const copy = strings.contractCompare;

// ------------------------------------------------------------------------------------------------ limits

/**
 * Per file: 10 MiB minus 64 KiB, as core checks it. The text says "10 MB" (a file in the 65 KB gap is refused as
 * "over 10 MB" -- accepted, D52). There is no limit on the two files together: core takes 21 MB in a request, so
 * two files at this size pass with their framing (D61).
 */
export const COMPARE_MAX_BYTES = 10 * 1024 * 1024 - 64 * 1024;

/**
 * The browser gives up at this point and aborts the request: the AI's 30 s plus the upload of two files at the limit
 * (about 33 s on a 5 Mbps line), D61.
 */
export const COMPARE_ABORT_MS = 80_000;

/** The countdown when a 429 / busy 503 carries no `retryAfter` of its own. */
export const DEFAULT_RETRY_AFTER_SECONDS = 10;

/** A `retryAfter` above this is the daily limit, not a wait: it gets its own frame and no countdown. */
export const LIMIT_FRAME_THRESHOLD_SECONDS = 60;

/**
 * A 429 this soon after the user's Cancel is the cancelled compare still running on the server (the AI's own
 * 30 s budget plus a margin; a worker thread cannot be cancelled), so it reads "still finishing".
 */
export const CANCEL_GRACE_MS = 35_000;

/** Change cards shown before "Show more". */
export const CARDS_PER_PAGE = 50;

/** File types the page accepts, lower case; the check ignores case. */
export const COMPARE_EXTENSIONS = [".pdf", ".docx"] as const;

// ------------------------------------------------------------------------------------------------- the API

export type CompareStatus =
  | "changes_found"
  | "no_changes"
  | "different_documents"
  | "not_compared";

export type CompareReason =
  | "no_text_original"
  | "no_text_revised"
  | "too_many_scanned_pages"
  | "unreadable_pages"
  | "original_unavailable"
  | "original_too_large"
  | "too_large";

export type CompareChangeType =
  | "changed"
  | "added"
  | "removed"
  | "moved"
  | "filled";

/** `original` is the old version, `revised` the new one. */
export type CompareDocument = "original" | "revised";

export interface CompareCounts {
  changed: number;
  added: number;
  removed: number;
  moved: number;
  filled: number;
}

export interface CompareSide {
  source: "library" | "upload";
  documentId: string | null;
  fileName: string;
  kind: "pdf" | "docx";
  /** `null` for a Word file and for a file that could not be read. */
  pages: number | null;
}

export interface CompareQuote {
  text: string;
  /** Set for a PDF, `null` for Word. */
  page: number | null;
  /** The clause heading, or "paragraph 12" for Word. */
  section: string | null;
  truncated: boolean;
}

export interface CompareSegment {
  op: "same" | "del" | "ins";
  text: string;
}

export interface CompareChange {
  id: string;
  type: CompareChangeType;
  section: string | null;
  /** `null` for an added clause. */
  before: CompareQuote | null;
  /** `null` for a removed clause. */
  after: CompareQuote | null;
  segments: CompareSegment[];
  explanation: string;
  explainedBy: "model" | "code";
  ocr: boolean;
}

export interface CompareWarning {
  /** A code this app does not know is ignored. */
  code: string;
  document: CompareDocument | null;
  pages: number[] | null;
}

export interface ContractCompareResponse {
  status: CompareStatus;
  reason: CompareReason | null;
  summary: string;
  counts: CompareCounts;
  original: CompareSide;
  revised: CompareSide;
  changes: CompareChange[];
  compared: "text";
  warnings: CompareWarning[];
}

// -------------------------------------------------------------------------------------------- the request

/** The old version: a contract picked from the user's own list, or a file. Only the active tab's is sent. */
export type OldVersion =
  | { kind: "library"; documentId: string }
  | { kind: "upload"; file: File };

export interface CompareInput {
  /** The new version -- always an upload. */
  revised: File;
  old: OldVersion;
}

// ------------------------------------------------------------------------------------------ the view-model

/** The three multipart parts an error can name. */
export type ComparePart = "revised" | "original" | "originalDocumentId";

export const COMPARE_PARTS: readonly ComparePart[] = [
  "revised",
  "original",
  "originalDocumentId",
];

/**
 * Why there is no result. Every kind carries the words to show in `message` except `busy`, whose words hold a
 * live countdown (`countdownMessage`), and the kinds that are not text at all (`gate`, `unauthorized`, `session`).
 */
export type CompareFailure =
  /** Inline under one file, or under the old-version choice for `originalDocumentId`. `dropPick`: a 404, the pick is gone. */
  | { kind: "file"; part: ComparePart; message: string; dropPick: boolean }
  /** Under the Compare button: a refusal that names no file. */
  | { kind: "general"; message: string }
  /** Busy: Compare re-enables when the countdown ends. `stillFinishing`: the user's cancelled compare is still running. */
  | { kind: "busy"; retryAfterSeconds: number; stillFinishing: boolean }
  /** The daily limit: the server's words, no countdown. */
  | { kind: "limit"; message: string }
  | { kind: "switchedOff"; message: string }
  /** The plan lacks AI Assistant (`plan`) or an administrator switched it off (`admin`): the banner, controls disabled. */
  | { kind: "gate"; gate: "plan" | "admin"; message: string | null }
  | { kind: "unauthorized" }
  | { kind: "session" }
  /** The service failed or could not be reached: a frame with Retry. */
  | { kind: "unavailable"; message: string }
  /** The request was not understood as multipart: reload, not retry. */
  | { kind: "reload"; message: string }
  | { kind: "timedOut"; message: string };

export interface CompareNotice {
  /** The warning's code, for a key and for tests. */
  code: string;
  text: string;
}

export interface ChangeCardModel {
  id: string;
  type: CompareChangeType | "other";
  /** "Changed" ... "Blank filled"; the raw type for one this app does not know. */
  typeLabel: string;
  /** The clause heading, or `null`. */
  heading: string | null;
  /** "old p.1 → new p.1", "new p.2", ... or `null` when the change carries no location. */
  location: string | null;
  before: CompareQuote | null;
  after: CompareQuote | null;
  segments: CompareSegment[];
  sentence: string;
  /** True when code, not the model, wrote the sentence: the card carries the "Sentence by code" tag. */
  byCode: boolean;
}

export type CompareFilterKey = "all" | CompareChangeType;

export interface CompareFilterChip {
  key: CompareFilterKey;
  label: string;
  /** The whole set: it counts every change found, also those past the 300 listed. */
  count: number;
}

export type CompareResultKind =
  | "changes"
  | "noChanges"
  | "differentDocuments"
  | "notCompared";

export interface CompareResult {
  kind: CompareResultKind;
  /** The service's `status`, as sent. */
  status: CompareStatus;
  /** The service's one-sentence summary ("6 changes: 2 changed, ..."). */
  summary: string;
  /** For every kind but `changes`: the words to show. */
  message: string;
  /** `notCompared`: the reason, as sent (`null` for the other kinds). */
  reason: string | null;
  /** `notCompared` with a stored copy that cannot be used: switch the old side to Upload. */
  switchToUpload: boolean;
  counts: CompareCounts;
  /** Every change found. */
  total: number;
  original: CompareSide | null;
  revised: CompareSide | null;
  changes: ChangeCardModel[];
  filters: CompareFilterChip[];
  notices: CompareNotice[];
  /** Whether code wrote a sentence the model should have written (for analytics). */
  sentencesByCode: boolean;
}

// ---------------------------------------------------------------------------------------- file validation

const extensionOf = (name: string): string => {
  const dot = name.lastIndexOf(".");
  return dot < 0 ? "" : name.slice(dot).toLowerCase();
};

export const hasAllowedExtension = (name: string): boolean =>
  (COMPARE_EXTENSIONS as readonly string[]).includes(extensionOf(name));

/**
 * "9 B", "340 KB", "1.2 MB", "10 MB". Rounded **up** to one decimal, so a size is never shown smaller than it is:
 * a file just over the limit must not read "9.9 MB" next to "10 MB". One helper for every size on the page (D52).
 */
export const formatFileSize = (bytes: number): string => {
  const safe = Number.isFinite(bytes) && bytes > 0 ? bytes : 0;
  if (safe < 1024) return `${safe} B`;
  if (safe < 1024 * 1024) return `${Math.ceil(safe / 1024)} KB`;
  const mb = Math.ceil((safe / (1024 * 1024)) * 10) / 10;
  return `${Number.isInteger(mb) ? mb : mb.toFixed(1)} MB`;
};

/**
 * What is wrong with one file, in words for the user -- or `null`. The server repeats these checks (and more:
 * damaged, password-protected); the browser answers the cheap ones before an upload of up to 10 MB.
 */
export const fileProblem = (file: File): string | null => {
  if (!hasAllowedExtension(file.name)) return copy.notPdfOrWord;
  if (file.size === 0) return copy.fileEmpty;
  if (file.size > COMPARE_MAX_BYTES) return copy.fileTooBig;
  return null;
};

/** The uploads of one compare, in the order they are sent, with the part each travels as. */
const uploadsOf = (
  input: CompareInput,
): { part: "revised" | "original"; file: File }[] => [
  ...(input.old.kind === "upload"
    ? [{ part: "original" as const, file: input.old.file }]
    : []),
  { part: "revised" as const, file: input.revised },
];

/**
 * The first reason this compare must not be sent, as the failure to show -- or `null`: a file the browser can already
 * refuse (type, empty, over the per-file bound), in words under that file. The two files together are not checked.
 */
export const validateCompareInput = (
  input: CompareInput,
): CompareFailure | null => {
  for (const { part, file } of uploadsOf(input)) {
    const message = fileProblem(file);
    if (message) return { kind: "file", part, message, dropPick: false };
  }
  return null;
};

// ---------------------------------------------------------------------------------------------- file names

/**
 * Every Unicode format character (category Cf): bidi embeddings, overrides, isolates and marks, zero-width spaces
 * and joiners, the soft hyphen, tag characters. None is drawn, yet each can reorder what is drawn around it or make
 * two names look identical (D59).
 */
const FORMAT_CHARACTERS = /\p{Cf}/gu;

/**
 * A file name as it is shown. Customer files carry any characters; a right-to-left override could make
 * `x\u202Excod.pdf` read as `xfdp.docx`, and a zero-width character could make two different names look the same in
 * the list. The page opens and downloads nothing, but the name beside the kind tile must not lie about the
 * extension, and the user must be able to tell two contracts apart. (A zero-width non-joiner inside a Persian or
 * Indic word goes too, so such a name may join its letters; the name stays readable.)
 */
export const safeFileName = (name: string): string => name.replace(FORMAT_CHARACTERS, "");

/**
 * A name inserted into the middle of a sentence, wrapped in first-strong isolate \u2026 pop isolate: a right-to-left name
 * cannot move the punctuation around it. Apply it to a name that already went through `safeFileName`.
 */
export const isolateName = (name: string): string => `\u2068${name}\u2069`;

// ------------------------------------------------------------------------------------ router state (D50)

/**
 * What Contract Search puts in the router state when the user presses "Compare with a new version", so the pick
 * is shown before the documents list has loaded. History state survives a refresh and a logout, so it carries the
 * user's key: a later login in the same browser never sees the previous user's file name (D58 c).
 */
export interface CompareRouterState {
  documentId: string;
  fileName: string;
  userKey: string;
}

/**
 * The key of the signed-in user for the router state: their id as text. No user (a logged-out store holds an empty
 * id) gives an empty key, which never matches, so nothing is carried.
 */
export const compareUserKey = (userId: unknown): string =>
  typeof userId === "number" || typeof userId === "string" ? String(userId).trim() : "";

const UUID_SHAPE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * A router state is used only when it has the shape above, the id is a UUID, and it was made for the current user
 * (`userKey`): anything else is ignored.
 */
export const readCompareRouterState = (
  value: unknown,
  userKey: string,
): CompareRouterState | null => {
  if (!userKey || typeof value !== "object" || value === null) return null;
  const { documentId, fileName, userKey: carried } = value as Record<string, unknown>;
  if (carried !== userKey) return null;
  if (typeof documentId !== "string" || !UUID_SHAPE.test(documentId)) return null;
  if (typeof fileName !== "string" || !fileName.trim()) return null;
  return { documentId, fileName: safeFileName(fileName), userKey };
};
